import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import Decimal from "decimal.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { LEAVE_PERIOD_LABEL, LEAVE_TYPES, LEAVE_TYPE_LABEL } from "../../api/types";
import type { LeaveCreate, LeavePeriod, LeaveRequest, LeaveType } from "../../api/types";
import { formatLeaveDays } from "./LeaveBalance";
import { clockText, epochDay, todayIso } from "./LeaveCalendar";

// 申請休假的對話框(第七輪 Task 3)。
//
// 業主原話:「同仁用他的帳號進系統申請休假(排假),填寫休假類別/說明,
// 框選休假日期(出現懸浮視窗日歷)」。
//
// --------------------------------------------------------------------------
// 天數:畫面算一份給人看,後端算一份算數
// --------------------------------------------------------------------------
// 送出去的 payload **沒有 days** —— 那是唯一會扣特休額度的數字,交給瀏覽器
// 決定的話誰都能把自己的特休寫成 0.1 天。這裡算的那一份只是「共 1.5 天」這行
// 預覽,讓人按下送出之前就看得到自己請了幾天;算法與 `app/leave.py` 同一套
// (一天兩格,上午 0、下午 1),所以預覽與後端存下來的永遠一致。
//
// 一律 decimal.js。半天假讓天數出現 0.5,而 float 的 0.1 + 0.2 不是 0.3。
//
// --------------------------------------------------------------------------
// 講不通的寫法在這裡就擋下
// --------------------------------------------------------------------------
// 迄日早於起日、或是「第一天只請上午、後面幾天卻整天」這種寫法,後端會回 422。
// 前端先擋一次不是為了取代它,而是為了不要讓人等一趟往返才知道打錯。
//
// --------------------------------------------------------------------------
// 擋下來的理由一定要「說得出是哪一格」
// --------------------------------------------------------------------------
// 錯誤訊息不是印一段紅字就算數。它掛在**出錯的那一格**上(`error` 交給共用的
// `Input` / `Select`,它們會產生 aria-invalid 與 aria-describedby),
// 並且宣告成即時區域 —— 錯誤往往是別的欄位造成的(改完迄日,錯的是這一格與
// 起日的關係),不播報的話讀螢幕的人要等 Tab 走回去才知道。
//
// 送出鈕用 `aria-disabled` 而**不是** `disabled`:disabled 的按鈕退出 Tab 序,
// 鍵盤使用者走完整份表單找不到送出鈕,連「按下去被拒絕」這個線索都沒有。
// 改成按得下去、在 onSubmit 內攔截,並把焦點送到出錯的那一格。

/** 一天兩格,所以每一格就是 0.5 天。 */
const HALF_DAY = new Decimal("0.5");

/**
 * 按小時請假(業主 2026-11-03):「最小單位是 1 小時 一天算8小時」,
 * 填幾點到幾點(「如果是時數 應該要多 幾點到幾點」),時數由兩者相減。
 */
export const HOURS_PER_DAY = 8;

type LeaveUnit = "DAYS" | "HOURS";

const UNIT_OPTIONS: { value: LeaveUnit; label: string }[] = [
  { value: "DAYS", label: "整天/半天" },
  { value: "HOURS", label: "按小時" },
];

/** 整點的下拉:開始 00:00~23:00、結束 01:00~24:00(不跨到隔天)。 */
const START_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({ value: String(hour), label: clockText(hour) }));
const END_OPTIONS = Array.from({ length: 24 }, (_, i) => ({ value: String(i + 1), label: clockText(i + 1) }));

const PERIOD_OPTIONS = (["FULL", "AM", "PM"] as LeavePeriod[]).map((value) => ({
  value,
  label: LEAVE_PERIOD_LABEL[value],
}));

const TYPE_OPTIONS = LEAVE_TYPES.map((value) => ({ value, label: LEAVE_TYPE_LABEL[value] }));

/** 出錯時該標在哪一格。錯誤訊息與焦點都跟著它走。 */
export type LeaveField = "start_date" | "end_date" | "start_period" | "end_period" | "end_hour";

export interface LeaveSpan {
  /** 天數字串,以 0.5 為單位,例 `"1.5"`;算不出來時是 null。 */
  days: string | null;
  /** 算不出來的原因,可以直接顯示給使用者看。 */
  error: string | null;
  /**
   * 這個錯誤該標在哪一格。沒有錯誤時是 null。
   *
   * **只指一格。** 同一句話同時掛在四個欄位上,讀螢幕的人會連聽四次,
   * 而要改的其實只有一個地方。挑的是「使用者該去動的那一格」。
   */
  blame: LeaveField | null;
}

/**
 * 一段假共幾天,與後端 `app/leave.py` 的 `leave_days` 同一套算法。
 *
 * 把起訖換算成**以半天為刻度的 [起, 迄) 區間**:每一天佔兩格,上午是第 0 格、
 * 下午是第 1 格。天數就是格數 × 0.5。寫成格數而不是「迄日 − 起日 + 1 再減掉
 * 兩端的半天」,是因為後端也是這樣算的 —— 兩套算法遲早會對不起來。
 */
export function leaveDays(
  startDate: string,
  startPeriod: LeavePeriod,
  endDate: string,
  endPeriod: LeavePeriod,
): LeaveSpan {
  const start = epochDay(startDate);
  const end = epochDay(endDate);
  if (start === null) return { days: null, error: "請選起日與迄日", blame: "start_date" };
  if (end === null) return { days: null, error: "請選起日與迄日", blame: "end_date" };
  if (end < start) return { days: null, error: "迄日不能早於起日", blame: "end_date" };

  if (start !== end) {
    // 中間那幾天是整天,兩端卻缺了一半 —— 講不通。
    if (startPeriod === "AM") {
      return { days: null, error: "跨天的假,起日只能是整天或從下午開始", blame: "start_period" };
    }
    if (endPeriod === "PM") {
      return { days: null, error: "跨天的假,迄日只能是整天或到上午為止", blame: "end_period" };
    }
  }

  const startSlot = start * 2 + (startPeriod === "PM" ? 1 : 0);
  const endSlot = end * 2 + (endPeriod === "AM" ? 1 : 2);
  if (endSlot <= startSlot) {
    // 例如同一天「下午開始、上午結束」。零天的假單沒有意義,而且會讓額度扣了個寂寞。
    // 兩個時段都有份,標在起日時段上:改它就解得開,而訊息本身指名了兩格。
    return {
      days: null,
      error: "這段假算不出任何時數,請確認起訖的上午/下午",
      blame: "start_period",
    };
  }
  return { days: new Decimal(endSlot - startSlot).times(HALF_DAY).toString(), error: null, blame: null };
}

export interface LeaveDialogProps {
  open: boolean;
  onClose: () => void;
}

export function LeaveDialog({ open, onClose }: LeaveDialogProps) {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const fieldId = useId();
  const reasonId = `${fieldId}-reason`;

  // 擋下來時把焦點送到出錯的那一格。四個控制項各留一個 ref ——
  // 「按了送出卻什麼都沒發生」對鍵盤使用者等於整份表單壞掉。
  const startDateRef = useRef<HTMLInputElement | null>(null);
  const endDateRef = useRef<HTMLInputElement | null>(null);
  const startPeriodRef = useRef<HTMLSelectElement | null>(null);
  const endPeriodRef = useRef<HTMLSelectElement | null>(null);
  const endHourRef = useRef<HTMLSelectElement | null>(null);

  const [leaveType, setLeaveType] = useState<LeaveType>("ANNUAL");
  const [reason, setReason] = useState("");
  const [startDate, setStartDate] = useState(todayIso);
  const [startPeriod, setStartPeriod] = useState<LeavePeriod>("FULL");
  const [endDate, setEndDate] = useState(todayIso);
  const [endPeriod, setEndPeriod] = useState<LeavePeriod>("FULL");
  const [unit, setUnit] = useState<LeaveUnit>("DAYS");
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(10);
  const [error, setError] = useState<string | null>(null);

  // 每次開啟都從今天、整天、特休開始。上一次打到一半的東西不會殘留 ——
  // 「我明明取消了,怎麼還記得我上次要請的日子」是最容易誤送的一種。
  useEffect(() => {
    if (!open) return;
    setLeaveType("ANNUAL");
    setReason("");
    setStartDate(todayIso());
    setStartPeriod("FULL");
    setEndDate(todayIso());
    setEndPeriod("FULL");
    setUnit("DAYS");
    setStartHour(9);
    setEndHour(10);
    setError(null);
  }, [open]);

  const hourly = unit === "HOURS";
  const hours = endHour - startHour;
  // 按小時:同一天、整天時段,天數 = 時數 / 8(與後端 app/leave.py 同一套)
  const span: LeaveSpan = !hourly
    ? leaveDays(startDate, startPeriod, endDate, endPeriod)
    : epochDay(startDate) === null
      ? { days: null, error: "請選日期", blame: "start_date" }
      : hours < 1
        ? { days: null, error: "結束要晚於開始", blame: "end_hour" }
        : hours > HOURS_PER_DAY
          ? { days: null, error: `按小時最多 ${HOURS_PER_DAY} 小時;超過請改請整天`, blame: "end_hour" }
          : { days: new Decimal(hours).dividedBy(HOURS_PER_DAY).toString(), error: null, blame: null };
  const sameDay = startDate === endDate && startDate !== "";

  /** 這一格要顯示的錯誤;不是出錯的那一格就是 null。 */
  const errorFor = (field: LeaveField): string | null => (span.blame === field ? span.error : null);

  /** 起日往後挪時把迄日一起帶過去 —— 留在原地就變成「迄日早於起日」。 */
  const changeStartDate = (value: string) => {
    setStartDate(value);
    const next = epochDay(value);
    const current = epochDay(endDate);
    if (next !== null && (current === null || current < next)) setEndDate(value);
  };

  /**
   * 同一天的兩個時段一定要一致,所以改一邊就跟著改另一邊。
   *
   * 少了這一條,「同一天只請上午」得記得把兩個下拉都撥到上午;只撥了起日的人
   * 會得到起日上午、迄日整天 —— 那是一整天,而他以為自己只請了半天。
   */
  const changeStartPeriod = (value: LeavePeriod) => {
    setStartPeriod(value);
    if (sameDay) setEndPeriod(value);
  };

  const changeEndPeriod = (value: LeavePeriod) => {
    setEndPeriod(value);
    if (sameDay) setStartPeriod(value);
  };

  const create = useMutation({
    mutationFn: () => {
      const payload: LeaveCreate = hourly
        ? {
            leave_type: leaveType,
            start_date: startDate,
            start_period: "FULL",
            end_date: startDate,
            end_period: "FULL",
            start_hour: startHour,
            hours,
            reason,
          }
        : {
            leave_type: leaveType,
            start_date: startDate,
            start_period: startPeriod,
            end_date: endDate,
            end_period: endPeriod,
            reason,
          };
      return api.post<LeaveRequest>("/leave", payload);
    },
    onSuccess: () => {
      // 清單、日曆與額度三個地方都會變,一起請它們重撈。
      void queryClient.invalidateQueries({ queryKey: ["leave"] });
      void queryClient.invalidateQueries({ queryKey: ["leave-balance"] });
      push("休假申請已送出,等待老闆核准", "success");
      onClose();
    },
    // 後端的原句照抄(例如撞期的 409),前端不自己另編一句。
    onError: (err) => setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。"),
  });

  /** 出錯的那一格對應的控制項。 */
  const controlOf = (field: LeaveField): HTMLElement | null =>
    ({
      start_date: startDateRef.current,
      end_date: endDateRef.current,
      start_period: startPeriodRef.current,
      end_period: endPeriodRef.current,
      end_hour: endHourRef.current,
    })[field];

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (create.isPending) return;
    if (span.days === null) {
      // 按得下去、但不送出,而且把人帶到出錯的那一格 ——
      // 焦點一移過去,螢幕閱讀器就會唸出欄位名稱與那句錯誤(aria-describedby)。
      if (span.blame !== null) controlOf(span.blame)?.focus();
      return;
    }
    setError(null);
    create.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="申請休假"
      maxWidth="max-w-lg"
      // 打到一半誤點背景就整份消失太傷,關閉一律走 Esc 或明確的取消 / 關閉鈕。
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <Select
          label="假別"
          name="leave_type"
          options={TYPE_OPTIONS}
          value={leaveType}
          onChange={(event) => setLeaveType(event.target.value as LeaveType)}
        />

        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor={reasonId} className="text-sm font-medium text-[var(--text-2)]">
            說明
          </label>
          {/* 不設必填:生理假與產檢假尤其不該被要求交代理由。
              底下原本有一行「可留白」的提示,業主 2026-11-03 要拿掉。 */}
          <textarea
            id={reasonId}
            name="reason"
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="w-full min-w-0 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-2 text-sm text-[var(--text)]"
          />
        </div>

        {/* 按小時請假(業主 2026-11-03):最小單位 1 小時、一天算 8 小時、填幾點到幾點 */}
        <Select
          label="請假單位"
          name="unit"
          options={UNIT_OPTIONS}
          value={unit}
          onChange={(event) => setUnit(event.target.value as LeaveUnit)}
        />

        {hourly ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              ref={startDateRef}
              label="日期"
              name="start_date"
              type="date"
              value={startDate}
              error={errorFor("start_date")}
              errorRole="alert"
              onChange={(event) => changeStartDate(event.target.value)}
            />
            <Select
              label="開始"
              name="start_hour"
              options={START_OPTIONS}
              value={String(startHour)}
              onChange={(event) => setStartHour(Number(event.target.value))}
            />
            <Select
              ref={endHourRef}
              label="結束"
              name="end_hour"
              options={END_OPTIONS}
              value={String(endHour)}
              error={errorFor("end_hour")}
              errorRole="alert"
              onChange={(event) => setEndHour(Number(event.target.value))}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* 錯誤只掛在出錯的那一格上(errorFor),訊息因此整份表單只出現一次。 */}
            <Input
              ref={startDateRef}
              label="起日"
              name="start_date"
              type="date"
              value={startDate}
              error={errorFor("start_date")}
              errorRole="alert"
              onChange={(event) => changeStartDate(event.target.value)}
            />
            <Select
              ref={startPeriodRef}
              label="起日時段"
              name="start_period"
              options={PERIOD_OPTIONS}
              value={startPeriod}
              error={errorFor("start_period")}
              errorRole="alert"
              onChange={(event) => changeStartPeriod(event.target.value as LeavePeriod)}
            />
            <Input
              ref={endDateRef}
              label="迄日"
              name="end_date"
              type="date"
              value={endDate}
              error={errorFor("end_date")}
              errorRole="alert"
              onChange={(event) => setEndDate(event.target.value)}
            />
            <Select
              ref={endPeriodRef}
              label="迄日時段"
              name="end_period"
              options={PERIOD_OPTIONS}
              value={endPeriod}
              error={errorFor("end_period")}
              errorRole="alert"
              onChange={(event) => changeEndPeriod(event.target.value as LeavePeriod)}
            />
          </div>
        )}

        {/* 天數在按下送出之前就看得到。真正算數的那一份由後端算,兩邊同一套算法。
            算不出來時這裡不再印一次紅字 —— 那句話已經掛在出錯的那一格上了。 */}
        {span.days === null ? null : (
          <p className="m-0 text-sm text-[var(--text-2)]">
            {hourly ? `共 ${hours} 小時(${formatLeaveDays(span.days)} 天)` : `共 ${formatLeaveDays(span.days)} 天`}
          </p>
        )}

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={create.isPending}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            // aria-disabled 而不是 disabled:按得下去、Tab 走得到,
            // 攔截在 handleSubmit 裡並把焦點送到出錯的那一格。
            aria-disabled={span.days === null || undefined}
            className={span.days === null ? "opacity-55" : ""}
            loading={create.isPending}
            icon={<Send aria-hidden="true" size={16} />}
          >
            送出申請
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
